// src/components/NotificationDropdown.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useReminders } from '../context/ReminderContext';
import {
  Bell, Check, Clock, Trash2, ExternalLink,
  RotateCcw, CheckCircle2, Volume2
} from 'lucide-react';
import './notifications.css';

function formatRelativeTime(dateStr) {
  const target = new Date(dateStr);
  const now = new Date();
  const diffSec = Math.floor((target.getTime() - now.getTime()) / 1000);

  if (diffSec < -86400) {
    const days = Math.abs(Math.floor(diffSec / 86400));
    return { text: `${days}d overdue`, type: 'overdue' };
  }
  if (diffSec < -3600) {
    const hrs = Math.abs(Math.floor(diffSec / 3600));
    return { text: `${hrs}h overdue`, type: 'overdue' };
  }
  if (diffSec < -60) {
    const mins = Math.abs(Math.floor(diffSec / 60));
    return { text: `${mins}m overdue`, type: 'overdue' };
  }
  if (diffSec <= 60 && diffSec >= -60) {
    return { text: 'Due now', type: 'due' };
  }
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return { text: `In ${mins}m`, type: 'upcoming' };
  }
  if (diffSec < 86400) {
    const hrs = Math.floor(diffSec / 3600);
    return { text: `In ${hrs}h`, type: 'upcoming' };
  }
  return {
    text: target.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
    type: 'upcoming'
  };
}

export default function NotificationDropdown({ isOpen, onClose }) {
  const dropdownRef = useRef(null);
  const [tab, setTab] = useState('active'); // 'active' | 'completed'
  const {
    activeReminders,
    completedReminders,
    dueCount,
    completeReminder,
    snoozeReminder,
    deleteReminder,
    requestDesktopPermission
  } = useReminders();

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayList = tab === 'active' ? activeReminders : completedReminders;

  return (
    <div className="notification-panel" ref={dropdownRef}>
      {/* ── Header ── */}
      <div className="notification-header">
        <h4 className="notification-title">
          <Bell size={15} style={{ color: 'var(--primary)' }} />
          Reminders & Alerts
          {dueCount > 0 && (
            <span className="notification-badge">{dueCount} due</span>
          )}
        </h4>

        <button
          type="button"
          className="notification-action-btn"
          title="Enable desktop notifications"
          onClick={requestDesktopPermission}
        >
          <Volume2 size={12} />
          Alerts
        </button>
      </div>

      {/* ── Segmented Navigation Tabs ── */}
      <div className="notification-tabs">
        <button
          type="button"
          className={`notification-tab-btn ${tab === 'active' ? 'active' : ''}`}
          onClick={() => setTab('active')}
        >
          Active ({activeReminders.length})
        </button>
        <button
          type="button"
          className={`notification-tab-btn ${tab === 'completed' ? 'active' : ''}`}
          onClick={() => setTab('completed')}
        >
          Completed ({completedReminders.length})
        </button>
      </div>

      {/* ── Reminders List ── */}
      <div className="notification-list">
        {displayList.length === 0 ? (
          <div className="notification-empty">
            <CheckCircle2 size={32} style={{ color: 'var(--success)', opacity: 0.6, marginBottom: 8 }} />
            <div className="fw-semibold" style={{ fontSize: 13, color: 'var(--text)' }}>
              {tab === 'active' ? 'All caught up!' : 'No completed reminders'}
            </div>
            <div className="small text-muted mt-1">
              {tab === 'active'
                ? 'No pending reminders scheduled at this moment.'
                : 'Completed reminders will be listed here.'}
            </div>
          </div>
        ) : (
          displayList.map((r) => {
            const rel = formatRelativeTime(r.remindAt);
            const isDue = rel.type === 'due' && !r.isCompleted;
            const isOverdue = rel.type === 'overdue' && !r.isCompleted;

            return (
              <div
                key={r.id}
                className={`notification-item ${isDue ? 'is-due' : ''} ${isOverdue ? 'is-overdue' : ''}`}
              >
                <div className="notification-item-top">
                  <span
                    className="notification-lead-link"
                    onClick={() => {
                      onClose();
                      window.location.href = `/dashboard/leads/${r.leadId}`;
                    }}
                    title="Open lead details"
                  >
                    {r.leadName || 'Lead'}
                    <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </span>

                  <span className={`notification-time-badge ${rel.type}`}>
                    {rel.text}
                  </span>
                </div>

                <div className="notification-note">
                  {r.title}
                </div>

                <div className="notification-item-actions">
                  {!r.isCompleted ? (
                    <>
                      <button
                        type="button"
                        className="notification-action-btn complete"
                        title="Mark as done"
                        onClick={() => completeReminder(r.id, true)}
                      >
                        <Check size={12} /> Done
                      </button>

                      <button
                        type="button"
                        className="notification-action-btn"
                        title="Postpone 15 mins"
                        onClick={() => snoozeReminder(r.id, 15)}
                      >
                        <Clock size={11} /> +15m
                      </button>

                      <button
                        type="button"
                        className="notification-action-btn"
                        title="Postpone 1 hour"
                        onClick={() => snoozeReminder(r.id, 60)}
                      >
                        <Clock size={11} /> +1h
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="notification-action-btn"
                      title="Reopen reminder"
                      onClick={() => completeReminder(r.id, false)}
                    >
                      <RotateCcw size={11} /> Reopen
                    </button>
                  )}

                  <button
                    type="button"
                    className="notification-action-btn delete"
                    title="Delete reminder"
                    onClick={() => deleteReminder(r.id)}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
